import { CookiesKeysTypes } from '../constants/cookiesKeys';
import { Environment } from '../constants/environments';
import { readCredential } from './credentialsStorage';

type GetCookieValue = {
  cookieKey: CookiesKeysTypes;
  needPrefix?: boolean;
};

/**
 * Retrieves the value of a specified cookie.
 *
 * @param {GetCookieValue} { cookieKey, needPrefix = true } Object containing the cookie key and whether a prefix is needed.
 * @returns {string} The value of the cookie or an empty string if the cookie is not found.
 *
 * This function constructs a cookie key possibly prefixed with a dynamic environment-related string, then searches
 * for this cookie in the document's cookie string. It logs an error if the cookie is not found.
 */
const getCookieValue = ({ cookieKey, needPrefix = true }: GetCookieValue): string => {
  const { ENVIRONMENT } = Environment;
  const environmentSuffix = ENVIRONMENT && ENVIRONMENT !== 'production' ? `_${String(ENVIRONMENT)}_` : '_';
  const keyCookie = `ing_1${environmentSuffix}${encodeURIComponent(cookieKey)}`;

  const cookieMap = document.cookie.split(';')
    .reduce((acc, cookie) => {
      const [key, value] = cookie.trim().split('=');
      acc[key] = value;
      return acc;
    }, {} as Record<string, string>);

  const resolvedKey = needPrefix ? keyCookie : cookieKey;
  const cookieValue = cookieMap[resolvedKey];

  if (cookieValue) return cookieValue;

  // Cookie missing: in a cross-site iframe the browser drops it on write. That is the
  // only case where `readCredential` returns anything — see credentialsStorage.ts.
  return readCredential(resolvedKey) ?? '';
};

export default getCookieValue;
